import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import { fetchTodayQueue } from './api';

/**
 * The live queue for the active branch. Branch-scoped — see
 * HMS_V4_API_CONTRACT.md §5 and src/lib/queryKeys.ts.
 *
 * `branchVersion` is appended to the key deliberately, not just relied on via
 * branchKey()'s internal getActiveBranchId() read. See queryKeys.ts's
 * invalidateBranchScoped docstring for the race this closes: without it, a
 * branch switch could refetch under a stale key before this hook's own
 * component re-renders to pick up the new one. Depending on branchVersion —
 * the same value every legacy branch-scoped screen already keys its refetch
 * effect on (see the old QueueView.jsx) — guarantees this hook recomputes at
 * the right moment instead of racing it.
 *
 * refetchInterval matches the 30s cadence App.jsx's sidebar "waiting" badge
 * has always polled at (see its own separate getTodayQueue() call) — not a
 * new polling frequency, just extending the existing one to the list itself,
 * which today does NOT auto-refresh once mounted.
 */
export function useTodayQueue(patientId?: number) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.queue.today(), branchVersion, patientId ?? null],
    queryFn: () => fetchTodayQueue(patientId),
    refetchInterval: 30_000,
  });
}
